class Impulsa3IngredientGallery {
  constructor(section) {
    this.section = section;
    this.triggers = Array.from(section.querySelectorAll('[data-ingredient-trigger]'));
    this.modal = section.querySelector('[data-ingredient-modal]');
    this.dialog = section.querySelector('[data-ingredient-dialog]');
    this.content = section.querySelector('.impulsa3-ingredient-gallery-modal__content');
    this.closeButtons = Array.from(section.querySelectorAll('[data-ingredient-close]'));
    this.items = Array.from(section.querySelectorAll('[data-ingredient-item]'));
    this.filters = Array.from(section.querySelectorAll('.impulsa3-ingredient-gallery__filter'));

    this.prevNavButton = section.querySelector('[data-ingredient-prev]');
    this.nextNavButton = section.querySelector('[data-ingredient-next]');

    this.activeHandle = null;
    this.lastTrigger = null;
    this.isAnimating = false;
    this.animationDuration = 350;
    this.activeFilter = 'all';

    if (!this.section || !this.modal || !this.dialog || !this.content || !this.triggers.length || !this.items.length) return;

    this.bindEvents();
    this.initTabs();
  }

  bindEvents() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleTriggerClick);
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.handleCloseClick);
    });

    if (this.prevNavButton) {
      this.prevNavButton.addEventListener('click', this.handlePrevClick);
    }

    if (this.nextNavButton) {
      this.nextNavButton.addEventListener('click', this.handleNextClick);
    }

    this.filters.forEach((filter) => {
      filter.addEventListener('click', this.handleFilterClick);
    });

    document.addEventListener('keydown', this.handleKeydown);
  }

  handleFilterClick = (event) => {
    const filter = event.currentTarget.getAttribute('data-filter');
    if (!filter) return;
    this.applyFilter(filter);
  };

  matchesFilter(productValue, filter) {
    if (filter === 'all') return true;
    const product = (productValue || '').toLowerCase();
    return product === filter || product === 'todos';
  }

  applyFilter(filter) {
    this.activeFilter = filter;

    this.filters.forEach((btn) => {
      const isActive = btn.getAttribute('data-filter') === filter;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    this.triggers.forEach((trigger) => {
      const product = trigger.getAttribute('data-product');
      const matches = this.matchesFilter(product, filter);
      trigger.style.display = matches ? '' : 'none';
    });
  }

  getVisibleItems() {
    return this.items.filter((item) =>
      this.matchesFilter(item.getAttribute('data-product'), this.activeFilter)
    );
  }

  initTabs() {
    this.items.forEach((item) => {
      this.bindTabs(item);
      this.resetTabs(item);
    });
  }

  bindTabs(item) {
    const buttons = Array.from(item.querySelectorAll('[data-tab-button]'));
    const panels = Array.from(item.querySelectorAll('[data-tab-panel]'));

    if (!buttons.length || !panels.length) return;

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = button.getAttribute('data-tab-button');
        this.activateTab(item, target);
      });
    });
  }

  activateTab(item, target) {
    const buttons = Array.from(item.querySelectorAll('[data-tab-button]'));
    const panels = Array.from(item.querySelectorAll('[data-tab-panel]'));

    if (!buttons.length || !panels.length) return;

    buttons.forEach((button) => {
      const isActive = button.getAttribute('data-tab-button') === target;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-tab-panel') === target;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
  }

  resetTabs(item) {
    const buttons = Array.from(item.querySelectorAll('[data-tab-button]'));
    const panels = Array.from(item.querySelectorAll('[data-tab-panel]'));

    if (!buttons.length || !panels.length) return;

    const firstButton = buttons[0];
    const firstTarget = firstButton ? firstButton.getAttribute('data-tab-button') : null;

    if (!firstTarget) return;

    this.activateTab(item, firstTarget);
  }

  destroy() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleTriggerClick);
    });

    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.handleCloseClick);
    });

    if (this.prevNavButton) {
      this.prevNavButton.removeEventListener('click', this.handlePrevClick);
    }

    if (this.nextNavButton) {
      this.nextNavButton.removeEventListener('click', this.handleNextClick);
    }

    this.filters.forEach((filter) => {
      filter.removeEventListener('click', this.handleFilterClick);
    });

    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleTriggerClick = (event) => {
    const trigger = event.currentTarget;
    const handle = trigger.getAttribute('data-ingredient-handle');

    if (!handle) return;

    this.lastTrigger = trigger;
    this.openModal(handle);
  };

  handleCloseClick = () => {
    this.closeModal();
  };

  handlePrevClick = () => {
    this.navigateToIngredient(-1);
  };

  handleNextClick = () => {
    this.navigateToIngredient(1);
  };

  handleKeydown = (event) => {
    if (this.modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateToIngredient(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateToIngredient(1);
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  setContentHeight(height) {
    if (!this.content) return;
    this.content.style.height = `${height}px`;
  }

  resetContentHeight() {
    if (!this.content) return;
    this.content.style.height = 'auto';
  }

  resetItemState(item) {
    item.classList.remove(
      'is-active-view',
      'is-animating',
      'is-entering-from-next',
      'is-entering-from-prev',
      'is-leaving-to-next',
      'is-leaving-to-prev'
    );
  }

  resetAllItems() {
    this.items.forEach((item) => {
      this.resetItemState(item);
    });
  }

  openModal(handle) {
    const targetItem = this.items.find(
      (item) => item.getAttribute('data-ingredient-handle') === handle
    );

    if (!targetItem) return;

    this.activeHandle = handle;
    this.isAnimating = false;

    this.items.forEach((item) => {
      const isActive = item.getAttribute('data-ingredient-handle') === handle;
      this.resetItemState(item);
      item.hidden = !isActive;

      if (isActive) {
        item.classList.add('is-active-view');
        this.resetTabs(item);
      }
    });

    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('impulsa3-ingredient-gallery-modal-open');

    this.updateNavState();

    requestAnimationFrame(() => {
      this.setContentHeight(targetItem.offsetHeight);
      requestAnimationFrame(() => {
        this.resetContentHeight();
        this.dialog.focus();
      });
    });
  }

  closeModal() {
    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('impulsa3-ingredient-gallery-modal-open');
    this.isAnimating = false;
    this.resetAllItems();
    this.resetContentHeight();

    if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
  }

  navigateToIngredient(direction) {
    if (this.isAnimating || !this.activeHandle) return;

    const navItems = this.getVisibleItems();
    if (navItems.length <= 1) return;

    const currentIndex = navItems.findIndex(
      (item) => item.getAttribute('data-ingredient-handle') === this.activeHandle
    );

    if (currentIndex === -1) return;

    let nextIndex = currentIndex + direction;

    if (nextIndex < 0) nextIndex = navItems.length - 1;
    if (nextIndex >= navItems.length) nextIndex = 0;

    const currentItem = navItems[currentIndex];
    const nextItem = navItems[nextIndex];

    if (!currentItem || !nextItem || currentItem === nextItem) return;

    const leavingClass = direction > 0 ? 'is-leaving-to-next' : 'is-leaving-to-prev';
    const enteringClass = direction > 0 ? 'is-entering-from-next' : 'is-entering-from-prev';

    this.isAnimating = true;

    const currentHeight = currentItem.offsetHeight;

    this.resetItemState(currentItem);
    this.resetItemState(nextItem);

    currentItem.hidden = false;
    nextItem.hidden = false;

    this.resetTabs(nextItem);

    currentItem.classList.add('is-animating', 'is-active-view');
    nextItem.classList.add('is-animating', enteringClass);

    this.setContentHeight(currentHeight);

    requestAnimationFrame(() => {
      const nextHeight = nextItem.offsetHeight;
      this.setContentHeight(nextHeight);

      requestAnimationFrame(() => {
        currentItem.classList.add(leavingClass);
        currentItem.classList.remove('is-active-view');

        nextItem.classList.add('is-active-view');
        nextItem.classList.remove(enteringClass);
      });
    });

    window.setTimeout(() => {
      currentItem.hidden = true;

      this.resetItemState(currentItem);
      this.resetItemState(nextItem);

      nextItem.hidden = false;
      nextItem.classList.add('is-active-view');

      this.activeHandle = nextItem.getAttribute('data-ingredient-handle');
      this.isAnimating = false;
      this.updateNavState();
      this.resetContentHeight();
    }, this.animationDuration);
  }

  updateNavState() {
    const disabled = this.getVisibleItems().length <= 1;

    if (this.prevNavButton) {
      this.prevNavButton.disabled = disabled;
    }

    if (this.nextNavButton) {
      this.nextNavButton.disabled = disabled;
    }
  }

  trapFocus(event) {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];

    const focusableElements = Array.from(
      this.dialog.querySelectorAll(focusableSelectors.join(','))
    ).filter((el) => el.offsetParent !== null);

    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const current = document.activeElement;

    if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

class Impulsa3IngredientGalleryManager {
  constructor() {
    this.instances = new Map();
    this.init();
  }

  init() {
    const sections = document.querySelectorAll('.impulsa3-ingredient-gallery');

    sections.forEach((section) => {
      this.mount(section);
    });

    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('shopify:section:unload', this.handleSectionUnload);
  }

  mount(section) {
    if (!section || this.instances.has(section)) return;

    const instance = new Impulsa3IngredientGallery(section);
    this.instances.set(section, instance);
  }

  unmount(section) {
    const instance = this.instances.get(section);

    if (!instance) return;

    instance.destroy();
    this.instances.delete(section);
  }

  handleSectionLoad = (event) => {
    const directSection = event.target;
    const ingredientSection = directSection.classList?.contains('impulsa3-ingredient-gallery')
      ? directSection
      : directSection.querySelector('.impulsa3-ingredient-gallery');

    if (ingredientSection) {
      this.mount(ingredientSection);
    }
  };

  handleSectionUnload = (event) => {
    const directSection = event.target;
    const ingredientSection = directSection.classList?.contains('impulsa3-ingredient-gallery')
      ? directSection
      : directSection.querySelector('.impulsa3-ingredient-gallery');

    if (ingredientSection) {
      this.unmount(ingredientSection);
    }
  };
}

if (!window.impulsa3IngredientGalleryManager) {
  window.impulsa3IngredientGalleryManager = new Impulsa3IngredientGalleryManager();
}